import React from "react";
import Box from "@mui/joy/Box";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Game } from "../../../../models/games";
import { ChartGranularity } from "../../../../models/bidAnalytics";
import { toDatetimeLocalValue } from "../format";

export type AnalyticsFiltersState = {
    from: Date;
    to: Date;
    gameId: number | undefined;
    take: number;
    stuckHours: number;
    granularity: ChartGranularity;
};

export type AnalyticsFiltersProps = {
    value: AnalyticsFiltersState;
    onChange: (next: AnalyticsFiltersState) => void;
    onRefresh: () => void;
    loading?: boolean;
    games: Game[];
    gamesLoading?: boolean;
    /** period — from/to; take — лимит топа; stuck — порог зависания */
    showPeriod?: boolean;
    showTake?: boolean;
    showStuckHours?: boolean;
    showGranularity?: boolean;
};

export function AnalyticsFilters(props: AnalyticsFiltersProps) {
    const {
        value,
        onChange,
        onRefresh,
        loading,
        games,
        gamesLoading,
        showPeriod = true,
        showTake = false,
        showStuckHours = false,
        showGranularity = false,
    } = props;

    return (
        <Box
            sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "flex-end",
                p: { xs: 1.5, sm: 2 },
                bgcolor: "background.level1",
                borderRadius: "md",
            }}
        >
            {showPeriod && (
                <>
                    <FormControl size="sm" sx={{ minWidth: { xs: "100%", sm: 180 }, flex: { xs: "1 1 100%", sm: "0 1 auto" } }}>
                        <FormLabel>Период с</FormLabel>
                        <Input
                            type="datetime-local"
                            value={toDatetimeLocalValue(value.from)}
                            onChange={(e) =>
                                onChange({ ...value, from: new Date(e.target.value) })
                            }
                        />
                    </FormControl>
                    <FormControl size="sm" sx={{ minWidth: { xs: "100%", sm: 180 }, flex: { xs: "1 1 100%", sm: "0 1 auto" } }}>
                        <FormLabel>Период по</FormLabel>
                        <Input
                            type="datetime-local"
                            value={toDatetimeLocalValue(value.to)}
                            onChange={(e) =>
                                onChange({ ...value, to: new Date(e.target.value) })
                            }
                        />
                    </FormControl>
                </>
            )}

            <FormControl size="sm" sx={{ minWidth: { xs: "100%", sm: 160 }, flex: { xs: "1 1 100%", sm: "0 1 auto" } }}>
                <FormLabel>Игра</FormLabel>
                {gamesLoading ? (
                    <CircularProgress size="sm" />
                ) : (
                    <Select
                        size="sm"
                        placeholder="Все игры"
                        startDecorator={<VideogameAssetIcon />}
                        value={value.gameId ?? -1}
                        onChange={(_, v) => {
                            const id = (v as number) ?? -1;
                            onChange({ ...value, gameId: id > 0 ? id : undefined });
                        }}
                    >
                        <Option value={-1}>Все</Option>
                        {games.map((g) => (
                            <Option key={g.id} value={g.id}>
                                {g.name}
                            </Option>
                        ))}
                    </Select>
                )}
            </FormControl>

            {showTake && (
                <FormControl size="sm" sx={{ minWidth: 100 }}>
                    <FormLabel>Сколько строк</FormLabel>
                    <Input
                        type="number"
                        slotProps={{ input: { min: 1, max: 200 } }}
                        value={value.take}
                        onChange={(e) =>
                            onChange({
                                ...value,
                                take: Math.max(1, Math.min(200, Number(e.target.value) || 20)),
                            })
                        }
                    />
                </FormControl>
            )}

            {showGranularity && (
                <FormControl size="sm" sx={{ minWidth: 120 }}>
                    <FormLabel>Шаг графика</FormLabel>
                    <Select
                        size="sm"
                        value={value.granularity}
                        onChange={(_, v) =>
                            onChange({
                                ...value,
                                granularity: (v as ChartGranularity) ?? "hour",
                            })
                        }
                    >
                        <Option value="hour">Час</Option>
                        <Option value="day">День</Option>
                        <Option value="month">Месяц</Option>
                    </Select>
                </FormControl>
            )}

            {showStuckHours && (
                <FormControl size="sm" sx={{ minWidth: 140 }}>
                    <FormLabel>Зависла старше (ч)</FormLabel>
                    <Input
                        type="number"
                        slotProps={{ input: { min: 1, max: 720 } }}
                        value={value.stuckHours}
                        onChange={(e) =>
                            onChange({
                                ...value,
                                stuckHours: Math.max(1, Math.min(720, Number(e.target.value) || 24)),
                            })
                        }
                    />
                </FormControl>
            )}

            <Button
                size="sm"
                loading={loading}
                startDecorator={<RefreshIcon />}
                onClick={onRefresh}
                sx={{ width: { xs: "100%", sm: "auto" }, alignSelf: { sm: "flex-end" } }}
            >
                Обновить
            </Button>
        </Box>
    );
}
